/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ElementRef,
  QueryList,
  ViewChildren, AfterViewInit,
} from '@angular/core';
import { BrowserService } from '../../services/browser.service';
import { XdaStringsService } from '../../services/xda-strings.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { XdaTranslate } from '../../pipes/xda-translate.pipe';
import { OptionListOptions, Parameter } from '../../services/workflow.service';

@Component({
  selector: 'xrx-multi-select',
  templateUrl: './xrx-multi-select.component.html',
  styleUrls: ['./xrx-multi-select.component.less'],
  standalone: false,
})
export class XrxMultiSelectComponent implements OnInit, AfterViewInit {
  @Input() id: string;
  @Input() setting: Parameter;
  @Input() label: string;
  @Input() placeholder: string;
  @Input() list: OptionListOptions[] = [];
  @Input() localize: string;
  @Input() error: string;
  @Input() required: boolean;
  @Input() leftAlignText = false;
  @Input() isBordered = false;
  @Input() hasScrollbar = false;
  @Input() isMfd = false;
  @Input() maximumItemsSelected = -1;

  @Output() listChange = new EventEmitter<OptionListOptions>();
  @Output() change = new EventEmitter<OptionListOptions>();
  @Output() validation = new EventEmitter<Parameter>();

  scrollClass = '';

  private checkedItems: any;
  @ViewChildren('toLanguageItem') rowRefs: QueryList<ElementRef>;
  private xdaTranslatePipe: XdaTranslate;

  constructor(
    private xdaStringsService: XdaStringsService,
    public activeModal: NgbActiveModal,
    private browserService: BrowserService,
  ) {
    this.scrollClass = browserService.browserType() < 3 ? '' : 'nativeScroll';
    this.xdaTranslatePipe = new XdaTranslate(this.xdaStringsService);
  }

  ngOnInit() {
    if (!this.label && this.setting.label) {
      this.label = this.setting.label2 || '';
    }

    if (this.placeholder) {
      this.label = `${this.xdaTranslatePipe.transform(this.label)} (${this.xdaTranslatePipe.transform(this.placeholder, ['5'])})`;
    }

    this.checkedItems = this.list.map((item) => (item.checked ? true : false));
  }

  ngAfterViewInit() {
    this.fade();
  }

  onChecked(value: boolean, item: OptionListOptions) {
    item.checked = value;
    this.change.emit(item);
  }

  rowClicked(item: OptionListOptions) {
    if (
      item.checked ||
      this.list.filter((x) => x.checked === true).length <
        this.maximumItemsSelected
    ) {
      item.checked = !item.checked;
      this.change.emit(item);
    }

    this.validate();
    this.fade();
  }

  submitChanges() {
    this.validate();
    this.activeModal.dismiss();
  }

  cancel() {
    for (let i = 0; i < this.list.length; i++) {
      this.list[i].checked = this.checkedItems[i];
    }
    this.change.emit(this.list[0]);
    this.activeModal.dismiss();
  }

  validate() {
    this.validation.emit(this.setting);
  }

  fadeUnselectedItem(item: OptionListOptions): boolean {
    if (this.maximumItemsSelected > -1) {
      if (
        this.list.filter((x) => x.checked === true).length >=
          this.maximumItemsSelected &&
        !item.checked
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return this.list.some((x) => x.checked == true) && !item.checked;
    }
  }

  fade() {
    this.list.forEach((item, index) => {
      if (this.fadeUnselectedItem(item)) {
        this.rowRefs
          .toArray()
          [index].nativeElement.classList.add('fade-unselected');
      } else {
        this.rowRefs
          .toArray()
          [index].nativeElement.classList.remove('fade-unselected');
      }
    });
  }

  getPropertyString(item: OptionListOptions): string {
    const translationString = item[this.localize as keyof typeof item];
    return translationString as string;
  }
}
